import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Grid,
  IconButton,
  InputAdornment,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  WhatsApp,
  Save,
  Edit,
  LocationOn,
  Place,
  Link as LinkIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Security,
  ExitToApp,
  Phone,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  YouTube,
  Language,
  ContactPhone,
  Email,
  AccessTime,
  Directions,
  Update,
  Backup,
  Restore,
  Settings,
  Lock,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// Custom JustDial Icon component
const JustDialIcon = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '14px',
      fontFamily: "'Poppins', sans-serif",
    }}
  >
    JD
  </Box>
);

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundImage: theme.palette.custom.cardGradient,
  backdropFilter: 'blur(10px)',
  '& .MuiInputBase-root': {
    '& input': {
      lineHeight: 'normal',
      verticalAlign: 'middle',
      padding: '8px 14px'
    },
    '& .MuiInputAdornment-root': {
      height: '100%',
      maxHeight: 'none',
      alignItems: 'center'
    }
  }
}));

const extractIframeSrc = (iframeCode) => {
  try {
    // Handle both full iframe code and direct URLs
    if (iframeCode.includes('<iframe')) {
      const srcMatch = iframeCode.match(/src=["'](.*?)["']/);
      return srcMatch ? srcMatch[1] : '';
    } else if (iframeCode.includes('google.com/maps/embed')) {
      return iframeCode;
    }
    return '';
  } catch (error) {
    return '';
  }
};

const extractLocationFromGoogleMapsUrl = (url) => {
  try {
    let locationInfo = {
      name: '',
      address: '',
      coordinates: '',
      placeId: ''
    };

    // Handle different Google Maps URL formats
    if (url.includes('/maps/')) {
      // Extract place ID
      const placeIdMatch = url.match(/place_id:([^\/\?]+)|place\/([^\/\?]+)/);
      if (placeIdMatch) {
        locationInfo.placeId = placeIdMatch[1] || placeIdMatch[2];
      }

      // Extract coordinates
      const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (coordMatch) {
        locationInfo.coordinates = `${coordMatch[1]},${coordMatch[2]}`;
      }

      // Extract location name and address
      let addressInfo = '';
      if (url.includes('/place/')) {
        addressInfo = url.split('/place/')[1].split('/')[0];
      } else if (url.includes('/search/')) {
        addressInfo = url.split('/search/')[1].split('/')[0];
      }

      if (addressInfo) {
        // Clean up the address info
        addressInfo = decodeURIComponent(addressInfo)
          .replace(/\+/g, ' ')
          .replace(/%20/g, ' ')
          .split('?')[0]
          .split('@')[0];

        // Parse address components
        const addressParts = addressInfo.split(',').map(part => part.trim());
        
        if (addressParts.length >= 3) {
          // Extract pincode if present
          const pincodeMatch = addressParts.join(' ').match(/\b\d{6}\b/);
          const pincode = pincodeMatch ? pincodeMatch[0] : '';

          // Find state (usually second last or last part)
          const stateIndex = addressParts.findIndex(part => 
            part.toLowerCase().includes('karnataka') ||
            part.toLowerCase().includes('kerala') ||
            part.toLowerCase().includes('tamil nadu') ||
            part.toLowerCase().includes('andhra pradesh')
          );

          if (stateIndex !== -1) {
            locationInfo.state = addressParts[stateIndex].trim();
            // City is usually before state
            locationInfo.city = addressParts[stateIndex - 1]?.trim() || 'Bengaluru';
            // Address is all parts before city
            locationInfo.address = addressParts.slice(0, stateIndex - 1).join(', ').trim();
          } else {
            // Fallback: assume standard format
            locationInfo.state = addressParts[addressParts.length - 1].replace(/\d{6}/, '').trim() || 'Karnataka';
            locationInfo.city = addressParts[addressParts.length - 2]?.trim() || 'Bengaluru';
            locationInfo.address = addressParts.slice(0, -2).join(', ').trim();
          }

          locationInfo.pincode = pincode;
          locationInfo.name = locationInfo.address.split(',')[0].trim(); // Use first part of address as name
        }
      }
    }

    // Validate extracted information
    if (!locationInfo.address && !locationInfo.coordinates) {
      throw new Error('Could not extract location details from the URL');
    }

    return locationInfo;
  } catch (error) {
    console.error('Error parsing Google Maps URL:', error);
    return null;
  }
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState(localStorage.getItem('whatsappNumber') || '7619678243');
  const [address, setAddress] = useState(localStorage.getItem('clinicAddress') || '');
  const [mapsLink, setMapsLink] = useState(localStorage.getItem('mapsLink') || '');
  const [isEditingWhatsapp, setIsEditingWhatsapp] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isEditingMapsLink, setIsEditingMapsLink] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPasswordWarning, setShowPasswordWarning] = useState(false);
  const [locations, setLocations] = useState(() => {
    const savedLocations = JSON.parse(localStorage.getItem('locations') || '[]');
    if (savedLocations.length === 0) {
      // If no locations exist, create one from contactInfo
      const contactInfo = JSON.parse(localStorage.getItem('contactInfo') || JSON.stringify({
        address: 'Begur - Hulimavu Rd, Raghavendra Layout, Akshayanagara West, Krishna Layout, Akshayanagar, Bengaluru, Karnataka 560076',
        mapLink: 'https://www.google.com/maps/place/Careflex+physiotherapy+(Homecare+physio)/@12.8739635,77.6139061,17z/data=!3m1!4b1!4m6!3m5!1s0x3bae6b0027e775d5:0xa49ed9408152cd22!8m2!3d12.8739635!4f77.6139061!16s%2Fg%2F11ww3d8_yp?entry=ttu',
        phone: '+91 7619678243',
        email: 'info@careflexphysiotherapy.com',
        hours: 'Mon - Sun: 7:00 AM - 9:00 PM'
      }));
      
      // Extract city and state from the address
      const addressParts = contactInfo.address.split(',');
      const city = addressParts[addressParts.length - 2]?.trim() || 'Bengaluru';
      const state = addressParts[addressParts.length - 1]?.trim().split(' ')[0] || 'Karnataka';
      
      return [{
        id: Date.now(),
        name: 'CareFlex Physiotherapy',
        branch: 'Main Branch',
        address: contactInfo.address,
        phone: contactInfo.phone,
        state: state,
        city: city,
        pincode: contactInfo.address.match(/\d{6}/)?.[0] || '560076',
        landmark: '',
        fullAddress: contactInfo.address
      }];
    }
    return savedLocations;
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    phone: '',
    branch: '',
    state: '',
    city: '',
    pincode: '',
    landmark: ''
  });
  const [socialLinks, setSocialLinks] = useState(JSON.parse(localStorage.getItem('socialLinks') || JSON.stringify({
    facebook: 'https://www.facebook.com/careflexphysiotherapy',
    instagram: 'https://www.instagram.com/careflexphysiotherapy',
    twitter: 'https://twitter.com/careflexphysio',
    linkedin: 'https://www.linkedin.com/company/careflexphysiotherapy',
    youtube: 'https://www.youtube.com/@careflexphysiotherapy',
    justdial: 'https://www.justdial.com/Bangalore/Careflex-Physiotherapy/080PXX80-XX80-250123152727-K2P3_BZDET'
  })));
  const [isEditingSocial, setIsEditingSocial] = useState(false);
  const [contactInfo, setContactInfo] = useState(JSON.parse(localStorage.getItem('contactInfo') || JSON.stringify({
    address: 'Begur - Hulimavu Rd, Raghavendra Layout, Akshayanagara West, Krishna Layout, Akshayanagar, Bengaluru, Karnataka 560076',
    mapLink: 'https://www.google.com/maps/place/Careflex+physiotherapy+(Homecare+physio)/@12.8739635,77.6139061,17z/data=!3m1!4b1!4m6!3m5!1s0x3bae6b0027e775d5:0xa49ed9408152cd22!8m2!3d12.8739635!4f77.6139061!16s%2Fg%2F11ww3d8_yp?entry=ttu',
    phone: '+91 7619678243',
    email: 'info@careflexphysiotherapy.com',
    hours: 'Mon - Sun: 7:00 AM - 9:00 PM'
  })));
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    // Initialize admin credentials if not set
    try {
      if (!localStorage.getItem('adminCredentials')) {
        localStorage.setItem('adminCredentials', JSON.stringify({
          username: 'admin',
          password: 'admin123'
        }));
      }
    } catch (error) {
      console.error('localStorage not available:', error);
    }
  }, []);

  useEffect(() => {
    if (locations.length > 0) {
      const mainLocation = locations[0];
      setContactInfo(prev => ({
        ...prev,
        address: mainLocation.address,
        phone: mainLocation.phone,
        mapLink: prev.mapLink // Keep existing map link
      }));
      
      // Update clinic address and maps link
      localStorage.setItem('clinicAddress', mainLocation.address);
      localStorage.setItem('mapsLink', contactInfo.mapLink);
    }
  }, [locations]);

  const handleLogin = (e) => {
    e.preventDefault();
    
    // Hardcoded default credentials for both development and production
    const defaultUsername = 'admin';
    const defaultPassword = 'admin123';

    // Simple direct comparison without localStorage
    if (username === defaultUsername && password === defaultPassword) {
      setIsLoggedIn(true);
      setError('');
      // Show password warning if using default password
      if (password === defaultPassword) {
        setShowPasswordWarning(true);
      }
    } else {
      setError('Invalid username or password. Please use: admin/admin123');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    setError('');
    setShowPasswordWarning(false);
  };

  const handlePasswordChange = () => {
    const defaultPassword = 'admin123';
    
    if (passwordData.currentPassword !== defaultPassword) {
      setError('Current password is incorrect');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    // Update credentials in localStorage
    try {
      localStorage.setItem('adminCredentials', JSON.stringify({
        username: 'admin',
        password: passwordData.newPassword
      }));
    } catch (error) {
      console.error('Failed to save new password:', error);
      setError('Failed to save new password. Please try again.');
      return;
    }

    // Reset form and show success message
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setOpenPasswordDialog(false);
    setSuccessMessage('Password updated successfully');
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage('');
    }, 3000);
  };

  const handleWhatsappNumberSave = () => {
    if (whatsappNumber.trim()) {
      localStorage.setItem('whatsappNumber', whatsappNumber);
      setShowSuccess(true);
      setIsEditingWhatsapp(false);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleAddressSave = () => {
    if (address.trim()) {
      localStorage.setItem('clinicAddress', address);
      setShowSuccess(true);
      setIsEditingAddress(false);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleMapsLinkSave = () => {
    if (mapsLink.trim()) {
      localStorage.setItem('mapsLink', mapsLink);
      setShowSuccess(true);
      setIsEditingMapsLink(false);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleAddLocation = () => {
    if (newLocation.name && newLocation.address) {
      const updatedLocations = [...locations];
      const newLocationObj = { 
        ...newLocation, 
        id: Date.now(),
        fullAddress: `${newLocation.address}, ${newLocation.landmark ? newLocation.landmark + ', ' : ''}${newLocation.city}, ${newLocation.state} - ${newLocation.pincode}`
      };

      if (locations.length === 0) {
        // If this is the first location, update contactInfo and other related storage
        setContactInfo(prev => ({
          ...prev,
          address: newLocation.address,
          phone: newLocation.phone,
          mapLink: prev.mapLink // Keep existing map link
        }));
        
        // Update clinic address and maps link
        localStorage.setItem('clinicAddress', newLocation.address);
        localStorage.setItem('mapsLink', contactInfo.mapLink);
      }

      updatedLocations.push(newLocationObj);
      setLocations(updatedLocations);
      localStorage.setItem('locations', JSON.stringify(updatedLocations));
      setNewLocation({ 
        name: '', 
        address: '', 
        phone: '',
        branch: '',
        state: '',
        city: '',
        pincode: '',
        landmark: ''
      });
      setOpenDialog(false);
      setShowSuccess(true);
      setSuccessMessage('Location added successfully!');
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    }
  };

  const handleDeleteLocation = (id) => {
    if (id === locations[0]?.id) {
      setError('Cannot delete the main location');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    const updatedLocations = locations.filter(location => location.id !== id);
    setLocations(updatedLocations);
    localStorage.setItem('locations', JSON.stringify(updatedLocations));
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleSocialLinksSave = () => {
    localStorage.setItem('socialLinks', JSON.stringify(socialLinks));
    setShowSuccess(true);
    setIsEditingSocial(false);
    setSuccessMessage('Social media links updated successfully!');
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage('');
    }, 3000);
  };

  const handleContactInfoSave = () => {
    // Update contactInfo in localStorage
    localStorage.setItem('contactInfo', JSON.stringify(contactInfo));
    
    // Update the main location if it exists
    if (locations.length > 0) {
      const updatedLocations = [...locations];
      updatedLocations[0] = {
        ...updatedLocations[0],
        address: contactInfo.address,
        phone: contactInfo.phone,
        fullAddress: contactInfo.address,
        city: contactInfo.address.split(',').slice(-2)[0]?.trim() || 'Bengaluru',
        state: contactInfo.address.split(',').slice(-1)[0]?.trim().split(' ')[0] || 'Karnataka',
        pincode: contactInfo.address.match(/\d{6}/)?.[0] || '560076'
      };
      setLocations(updatedLocations);
      localStorage.setItem('locations', JSON.stringify(updatedLocations));
    }
    
    // Update clinic address and maps link in localStorage
    localStorage.setItem('clinicAddress', contactInfo.address);
    localStorage.setItem('mapsLink', contactInfo.mapLink);
    
    setShowSuccess(true);
    setIsEditingContact(false);
    setSuccessMessage('Contact information updated successfully!');
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage('');
    }, 3000);
  };

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validatePhoneNumber = (phone) => {
    return /^\+?[1-9]\d{1,14}$/.test(phone);
  };

  const handleBackup = () => {
    const backup = {
      contactInfo,
      socialLinks,
      locations,
      whatsappNumber,
      address,
      mapsLink
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `careflex-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRestore = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const backup = JSON.parse(e.target.result);
        if (backup.contactInfo) setContactInfo(backup.contactInfo);
        if (backup.socialLinks) setSocialLinks(backup.socialLinks);
        if (backup.locations) setLocations(backup.locations);
        if (backup.whatsappNumber) setWhatsappNumber(backup.whatsappNumber);
        if (backup.address) setAddress(backup.address);
        if (backup.mapsLink) setMapsLink(backup.mapsLink);
        setShowSuccess(true);
        setSuccessMessage('Settings restored successfully!');
        setTimeout(() => {
          setShowSuccess(false);
          setSuccessMessage('');
        }, 3000);
      } catch (error) {
        setError('Invalid backup file');
      }
    };
    reader.readAsText(file);
  };

  const getDashboardStats = () => {
    return {
      totalLocations: locations.length,
      socialMediaLinks: Object.values(socialLinks).filter(link => link).length,
      contactInfoComplete: Object.values(contactInfo).every(value => value),
      lastUpdated: new Date().toLocaleDateString()
    };
  };

  const handleGoogleMapsLinkChange = (url) => {
    if (!url) return;

    const locationInfo = extractLocationFromGoogleMapsUrl(url);
    if (locationInfo) {
      setNewLocation(prev => ({
        ...prev,
        name: locationInfo.name || prev.name,
        address: locationInfo.address || prev.address,
        city: locationInfo.city || prev.city,
        state: locationInfo.state || prev.state,
        pincode: locationInfo.pincode || prev.pincode,
        mapLink: url
      }));

      // Show success message with details about what was extracted
      const extractedFields = [];
      if (locationInfo.address) extractedFields.push('address');
      if (locationInfo.city) extractedFields.push('city');
      if (locationInfo.state) extractedFields.push('state');
      if (locationInfo.pincode) extractedFields.push('pincode');

      setSuccessMessage(`Successfully extracted ${extractedFields.join(', ')} from Google Maps link`);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } else {
      setError('Could not extract location details. Please check the link format or fill in details manually.');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (!isLoggedIn) {
    return (
      <Container maxWidth="sm" sx={{ 
        mt: { xs: 4, sm: 8, md: 12 },
        mb: 4
      }}>
        <StyledCard sx={{
          py: 4,
          px: { xs: 2, sm: 4 },
          textAlign: 'center',
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }}>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              gutterBottom 
              color="primary"
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1.75rem', sm: '2.125rem' }
              }}
            >
              Admin Login
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Enter your credentials to access the admin dashboard
            </Typography>
          </Box>
          
          <Box 
            component="form" 
            onSubmit={handleLogin} 
            sx={{ 
              mt: 2,
              '& .MuiTextField-root': {
                mb: 2
              }
            }}
          >
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Security color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 1,
                  '& .MuiAlert-message': { width: '100%' }
                }}
              >
                {error}
              </Alert>
            )}
            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="large"
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #00796b, #009688)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #00695c, #00897b)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
                },
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
              }}
            >
              Login
            </Button>
          </Box>
        </StyledCard>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        pt: { xs: 8, sm: 9 },
        pb: 4,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1
      }}
    >
      {showPasswordWarning && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 2,
            borderRadius: 1,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setShowPasswordWarning(false);
                setOpenPasswordDialog(true);
              }}
            >
              Change Now
            </Button>
          }
        >
          <Typography variant="subtitle1" gutterBottom>
            Security Alert: Default Password in Use
          </Typography>
          <Typography variant="body2">
            You are currently using the default password. For security reasons, please change your password immediately.
          </Typography>
        </Alert>
      )}

      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 2,
          position: 'relative',
          zIndex: 2
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            flex: '1 1 100%',
            sm: { flex: '0 1 auto' }
          }}
        >
          Admin Dashboard
        </Typography>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={{ xs: 1, sm: 2 }}
          sx={{ 
            width: { xs: '100%', sm: 'auto' },
            '& .MuiButton-root': {
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '140px' },
              px: { sm: 2 },
              py: { xs: 1, sm: 1.2 },
              fontSize: { xs: '0.875rem', sm: '0.9rem' },
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }
            }
          }}
        >
          <Button
            variant="contained"
            startIcon={<Place />}
            onClick={() => navigate('/contact')}
            sx={{
              bgcolor: '#4CAF50',
              '&:hover': {
                bgcolor: '#45a049',
              },
            }}
          >
            View Contact Page
          </Button>
          <Button
            variant="outlined"
            startIcon={<Security />}
            onClick={() => setOpenPasswordDialog(true)}
            sx={{
              borderWidth: '1.5px',
              '&:hover': {
                borderWidth: '1.5px',
              }
            }}
          >
            Change Password
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ExitToApp />}
            onClick={handleLogout}
            sx={{
              borderWidth: '1.5px',
              '&:hover': {
                borderWidth: '1.5px',
              }
            }}
          >
            Logout
          </Button>
        </Stack>
      </Box>

      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: 3,
        backgroundColor: 'background.paper',
        borderRadius: '4px 4px 0 0',
        px: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        position: 'relative',
        zIndex: 1,
        '& .MuiInputBase-root': {
          '& input': {
            lineHeight: 'normal',
            verticalAlign: 'middle',
            padding: '8px 14px'
          },
          '& .MuiInputAdornment-root': {
            height: '100%',
            maxHeight: 'none',
            alignItems: 'center'
          }
        }
      }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          aria-label="admin dashboard tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              py: 1,
            }
          }}
        >
          <Tab label="Dashboard" value="dashboard" />
          <Tab label="Contact Info" value="contact" />
          <Tab label="Social Media" value="social" />
          <Tab label="Locations" value="locations" />
          <Tab label="Settings" value="settings" />
        </Tabs>
      </Box>

      <Box sx={{ 
        position: 'relative',
        zIndex: 1,
        flex: 1
      }}>
        {activeTab === 'dashboard' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <StyledCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Place color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Locations</Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {getDashboardStats().totalLocations}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active clinic locations
                </Typography>
              </StyledCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Language color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Social Media</Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {getDashboardStats().socialMediaLinks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connected platforms
                </Typography>
              </StyledCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ContactPhone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Contact Info</Typography>
                </Box>
                <Typography variant="h3" color="primary">
                  {getDashboardStats().contactInfoComplete ? '100%' : 'Incomplete'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Information status
                </Typography>
              </StyledCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StyledCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Update color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Last Updated</Typography>
                </Box>
                <Typography variant="h6" color="primary">
                  {getDashboardStats().lastUpdated}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last settings change
                </Typography>
              </StyledCard>
            </Grid>
          </Grid>
        )}
        
        {activeTab === 'contact' && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ContactPhone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Contact Information</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                    disabled={!isEditingContact}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={contactInfo.phone}
                    onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                    disabled={!isEditingContact}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Business Hours"
                    value={contactInfo.hours}
                    onChange={(e) => setContactInfo({ ...contactInfo, hours: e.target.value })}
                    disabled={!isEditingContact}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTime color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Main Address"
                    value={contactInfo.address}
                    onChange={(e) => setContactInfo({ ...contactInfo, address: e.target.value })}
                    disabled={!isEditingContact}
                    multiline
                    rows={3}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Place color="primary" sx={{ mt: 1 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Google Maps Link"
                    value={contactInfo.mapLink}
                    onChange={(e) => setContactInfo({ ...contactInfo, mapLink: e.target.value })}
                    disabled={!isEditingContact}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Directions color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {isEditingContact ? (
                      <Button
                        variant="contained"
                        onClick={handleContactInfoSave}
                        startIcon={<Save />}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => setIsEditingContact(true)}
                        startIcon={<Edit />}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                </Stack>
              </StyledCard>
            </Grid>
          </Grid>
        )}
        
        {activeTab === 'social' && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Language color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Social Media Links</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={showPreview ? <Edit /> : <Visibility />}
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? 'Edit Mode' : 'Preview Mode'}
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />
                {showPreview ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                    {Object.entries(socialLinks).map(([platform, link]) => {
                      if (!link) return null;
                      const Icon = {
                        facebook: Facebook,
                        instagram: Instagram,
                        twitter: Twitter,
                        linkedin: LinkedIn,
                        youtube: YouTube,
                        justdial: JustDialIcon
                      }[platform];
                      return (
                        <Tooltip key={platform} title={platform.charAt(0).toUpperCase() + platform.slice(1)}>
                          <Link
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 60,
                              height: 60,
                              bgcolor: 'white',
                              borderRadius: '50%',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                              }
                            }}
                          >
                            <Icon sx={{ fontSize: 28, color: 'primary.main' }} />
                          </Link>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Facebook"
                      value={socialLinks.facebook}
                      onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                      disabled={!isEditingSocial}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Facebook color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Instagram"
                      value={socialLinks.instagram}
                      onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                      disabled={!isEditingSocial}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Instagram color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Twitter"
                      value={socialLinks.twitter}
                      onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                      disabled={!isEditingSocial}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Twitter color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="LinkedIn"
                      value={socialLinks.linkedin}
                      onChange={(e) => setSocialLinks({ ...socialLinks, linkedin: e.target.value })}
                      disabled={!isEditingSocial}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkedIn color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="YouTube"
                      value={socialLinks.youtube}
                      onChange={(e) => setSocialLinks({ ...socialLinks, youtube: e.target.value })}
                      disabled={!isEditingSocial}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <YouTube color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="JustDial"
                      value={socialLinks.justdial}
                      onChange={(e) => setSocialLinks({ ...socialLinks, justdial: e.target.value })}
                      disabled={!isEditingSocial}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <JustDialIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {isEditingSocial ? (
                        <Button
                          variant="contained"
                          onClick={handleSocialLinksSave}
                          startIcon={<Save />}
                        >
                          Save
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          onClick={() => setIsEditingSocial(true)}
                          startIcon={<Edit />}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  </Stack>
                )}
              </StyledCard>
            </Grid>
          </Grid>
        )}
        
        {activeTab === 'locations' && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Place color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Clinic Locations</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                  >
                    Add Location
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <List>
                  {locations.map((location) => (
                    <ListItem
                      key={location.id}
                      sx={{
                        mb: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1,
                        flexDirection: 'column',
                        alignItems: 'stretch'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="h6" color="primary">
                            {location.name} {location.branch && `(${location.branch})`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {location.fullAddress}
                          </Typography>
                          {location.phone && (
                            <Typography variant="body2">
                              <Phone sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />
                              {location.phone}
                            </Typography>
                          )}
                        </Stack>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteLocation(location.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </StyledCard>
            </Grid>
          </Grid>
        )}
        
        {activeTab === 'settings' && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledCard>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Settings color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Backup & Restore</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Stack spacing={3}>
                  <Button
                    variant="contained"
                    startIcon={<Backup />}
                    onClick={handleBackup}
                  >
                    Backup Settings
                  </Button>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Restore />}
                  >
                    Restore Settings
                    <input
                      type="file"
                      hidden
                      accept=".json"
                      onChange={handleRestore}
                    />
                  </Button>
                </Stack>
              </StyledCard>
            </Grid>
          </Grid>
        )}
      </Box>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        sx={{ zIndex: 1300 }}
      >
        <DialogTitle>Add New Location</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <TextField
                fullWidth
                label="Google Maps Link"
                placeholder="Paste Google Maps link to auto-fill details"
                onChange={(e) => handleGoogleMapsLinkChange(e.target.value)}
                helperText="Paste a Google Maps link to automatically fill location details"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Place color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Tip: Open your location in Google Maps, click "Share" and copy the link
              </Typography>
            </Box>
            <Divider>
              <Typography variant="body2" color="textSecondary">
                Location Details
              </Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch Name"
                  value={newLocation.name}
                  onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                  placeholder="e.g., CareFlex Bengaluru"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch Code"
                  value={newLocation.branch}
                  onChange={(e) => setNewLocation({ ...newLocation, branch: e.target.value })}
                  placeholder="e.g., BLR-001"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={newLocation.state}
                  onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
                  placeholder="e.g., Karnataka"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={newLocation.city}
                  onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                  placeholder="e.g., Bengaluru"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  multiline
                  rows={2}
                  value={newLocation.address}
                  onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                  placeholder="e.g., #123, 1st Floor, Main Street"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Landmark"
                  value={newLocation.landmark}
                  onChange={(e) => setNewLocation({ ...newLocation, landmark: e.target.value })}
                  placeholder="e.g., Near City Mall"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={newLocation.pincode}
                  onChange={(e) => setNewLocation({ ...newLocation, pincode: e.target.value })}
                  placeholder="e.g., 560001"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={newLocation.phone}
                  onChange={(e) => setNewLocation({ ...newLocation, phone: e.target.value })}
                  placeholder="e.g., +91 9876543210"
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddLocation}
            disabled={!newLocation.name || !newLocation.address}
            sx={{
              background: 'linear-gradient(45deg, #00796b, #009688)',
              '&:hover': {
                background: 'linear-gradient(45deg, #00695c, #00897b)'
              }
            }}
          >
            Add Location
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={openPasswordDialog} 
        onClose={() => setOpenPasswordDialog(false)} 
        maxWidth="sm" 
        fullWidth
        sx={{ zIndex: 1300 }}
      >
        <DialogTitle>Change Admin Password</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePasswordChange}
            sx={{
              background: 'linear-gradient(45deg, #00796b, #009688)',
              '&:hover': {
                background: 'linear-gradient(45deg, #00695c, #00897b)'
              }
            }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {showSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed', 
            bottom: 24, 
            right: 24,
            zIndex: 1400
          }}
        >
          {successMessage || 'Settings updated successfully!'}
        </Alert>
      )}
    </Container>
  );
};

export default AdminPanel; 